// /embed/v1/records through the console's raw client: snake_case end to end,
// because titles are keyed by language tag (`uz-Cyrl`) and the shared
// client's camelizing would rewrite those keys.

import { useEffect, useState } from 'react';
import { api } from '@/modules/console/lib/api.js';

const BASE = 'embed/v1/records';

export const records = {
  access: () => api.get(`${BASE}/access`),
  catalogue: () => api.get(`${BASE}/catalogue`),
  plans: () => api.get(`${BASE}/plans`),
  plan: (id) => api.get(`${BASE}/plans/${id}`),
  createPlan: (data) => api.post(`${BASE}/plans`, { data }),
  updatePlan: (id, data) => api.patch(`${BASE}/plans/${id}`, { data }),
  deletePlan: (id) => api.del(`${BASE}/plans/${id}`),
  approve: (id) => api.post(`${BASE}/plans/${id}/approve`),
  document: (id, data) => api.post(`${BASE}/plans/${id}/document`, { data }),
  addCase: (planId, data) => api.post(`${BASE}/plans/${planId}/cases`, { data }),
  updateCase: (id, data) => api.patch(`${BASE}/cases/${id}`, { data }),
  deleteCase: (id) => api.del(`${BASE}/cases/${id}`),

  registers: () => api.get(`${BASE}/registers`),
  createRegister: (data) => api.post(`${BASE}/registers`, { data }),
  updateRegister: (id, data) => api.patch(`${BASE}/registers/${id}`, { data }),
  deleteRegister: (id) => api.del(`${BASE}/registers/${id}`),
  registerExport: (id) => `${BASE}/registers/${id}/export`,
  registrations: (params) => api.get(`${BASE}/registrations`, { params }),
  register: (data) => api.post(`${BASE}/registrations`, { data }),
  updateRegistration: (id, data) => api.patch(`${BASE}/registrations/${id}`, { data }),
  cancelRegistration: (id, reason) => api.post(`${BASE}/registrations/${id}/cancel`, { data: { reason } }),

  cases: (params) => api.get(`${BASE}/cases`, { params }),
  openCases: () => api.get(`${BASE}/cases/open`),
  caseDetail: (id) => api.get(`${BASE}/cases/${id}`),
  fileDocument: (caseId, fileId) => api.post(`${BASE}/cases/${caseId}/records`, { data: { file_id: fileId } }),
  fileFolder: (caseId, folderId, scope) => api.post(`${BASE}/cases/${caseId}/file-folder`, { data: { folder_id: folderId, scope } }),
  linkFolder: (caseId, folderId) => api.put(`${BASE}/cases/${caseId}/folder`, { data: { folder_id: folderId } }),
  closeCase: (id) => api.post(`${BASE}/cases/${id}/close`),
  reopenCase: (id, reason) => api.post(`${BASE}/cases/${id}/reopen`, { data: { reason } }),
  withdraw: (itemId, reason) => api.post(`${BASE}/items/${itemId}/withdraw`, { data: { reason } }),
  closePlan: (id) => api.post(`${BASE}/plans/${id}/close`),
  whereFiled: (fileId) => api.get(`${BASE}/files/${fileId}`),

  review: () => api.get(`${BASE}/review`),
  extend: (caseId, until, reason) => api.post(`${BASE}/cases/${caseId}/extend`, { data: { until, reason } }),
  hold: (caseId, reason) => api.post(`${BASE}/cases/${caseId}/hold`, { data: { reason } }),
  release: (caseId, reason) => api.post(`${BASE}/cases/${caseId}/release`, { data: { reason } }),
  acts: () => api.get(`${BASE}/acts`),
  createAct: (caseIds, note, kind = 'destruction') => api.post(`${BASE}/acts`, { data: { case_ids: caseIds, note: note || null, kind } }),
  transferQueue: () => api.get(`${BASE}/transfer`),
  packageLink: (actId) => api.get(`${BASE}/acts/${actId}/package`),
  rebuildPackage: (actId) => api.post(`${BASE}/acts/${actId}/package`),
  inventory: (data) => api.post(`${BASE}/inventory`, { data }),
  integrity: () => api.get(`${BASE}/integrity`),
  removeActCase: (actId, caseId) => api.del(`${BASE}/acts/${actId}/cases/${caseId}`),
  actDocument: (actId, language) => api.post(`${BASE}/acts/${actId}/document`, { data: { language } }),
  approveAct: (actId) => api.post(`${BASE}/acts/${actId}/approve`),
  cancelAct: (actId, reason) => api.post(`${BASE}/acts/${actId}/cancel`, { data: { reason } }),
  executeAct: (actId, confirm) => api.post(`${BASE}/acts/${actId}/execute`, { data: { confirm } }),

  // The project's people (the share dialog's search), for a card's executor.
  people: (search) => api.get('embed/v1/share/all-users', { params: search ? { search } : {} }),
};

const NONE = {
  enabled: false, can_read: false, can_file: false, can_manage: false, can_review: false, can_dispose: false,
  plan_approval: 'manual', who_files: 'records',
};
let cached = null;

// Whether the Records area exists for this person — asked once per page
// load (the answer changes only when an admin flips the policy or a role).
export function useRecordsAccess() {
  const [access, setAccess] = useState(cached);
  useEffect(() => {
    if (cached) return undefined;
    let cancelled = false;
    records.access().then((res) => {
      cached = res.success ? { ...NONE, ...res.response } : NONE;
      if (!cancelled) setAccess(cached);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return access;
}

export function forgetRecordsAccess() {
  cached = null;
}

export const showsRecords = (access) => Boolean(access?.enabled && access?.can_read);

export const canRegister = (access) => Boolean(access?.enabled && access?.can_file);

// Whether this person may file `node` into a case: records access, or — where
// the project lets editors file — edit access to the document. Folders: the
// server checks each document inside.
export function canFileNode(access, node, { canEdit = false } = {}) {
  if (!access?.enabled || node?.filedAt || node?.filed_at) return false;
  return Boolean(access.can_file || (access.who_files === 'editors' && canEdit));
}

// Whether the review (due cases, destruction acts) is this person's business.
export const showsReview = (access) => Boolean(access?.enabled && (access.can_review || access.can_dispose));
